<template>
  <TransitionRoot as="template" :show="isVisible">
    <Dialog class="relative z-50" @close="dismiss">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <img src="@/assets/images/summer_update_banner.jpg" :alt="notification.title" class="w-full object-cover" v-if="notification.key === 'summer_update'" />
                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div class="sm:flex sm:items-start">
                      <div v-if="notification.key !== 'summer_update'" class="mx-auto sm:mr-4 flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:size-10">
                        <InformationCircleIcon class="size-6 text-blue-600" aria-hidden="true" />
                      </div>
                      <div class="sm:mt-0 text-left">
                        <DialogTitle as="h3" class="text-base font-semibold text-gray-900">{{ notification.title }}</DialogTitle>
                        <div class="mt-2">
                            <template v-if="notification.key === 'summer_update'">
                                <p class="text-sm text-gray-600">Ми зробили профіль і галереї ще зручнішими:</p>
                                <p class="text-sm text-gray-600 mt-2 font-semibold">
                                    Основні зміни
                                </p>
                                <ul class="list-disc list-inside text-sm text-gray-500">
                                     <li>Короткі красиві адреси профілів: <a :href="`https://${userUrl}`" target="_blank" class="text-blue-500 hover:underline">{{ userUrl }}</a></li>
                                    <li>Оновлений дизайн галереї + зручне меню на мобільному</li>
                                    <li>Профіль з цінами та описами пакетів</li>
                                    <li>Категорії портфоліо (портрети, весілля, бренди тощо)</li>
                                    <li>Нова тема «Бежева» + компактні обгортки</li>
                                </ul>

                                <p class="text-sm text-gray-600 my-2">
                                    Дрібні зміни: дублювання файлів, оптимізація завантаження, сповіщення про збереження, нові шрифти, приховування дати.
                                </p>

                                <p class="text-sm text-gray-600">
                                    Хочете більше деталей? Переходьте в наші соцмережі: <a href="https://gallera.io/redirect/aHR0cHM6Ly9pbnN0YWdyYW0uY29tL2dhbGxlcmEuaW8%3D?from=summer_update" class="text-blue-500 hover:underline" target="_blank">Instagram</a> та <a href="https://gallera.io/redirect/aHR0cHM6Ly90Lm1lL2dhbGxlcmFpbw%3D%3D?from=summer_update" class="text-blue-500 hover:underline" target="_blank">Telegram</a>.
                                </p>
                            </template>
                            <p class="text-sm text-gray-500 space-y-2" v-html="notification.message" v-else></p>
                        </div>
                      </div>
                    </div>
                </div>
              <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  v-if="notification.button_text && notification.button_url"
                  type="button"
                  class="mb-3 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-500 sm:mb-0 sm:w-auto cursor-pointer"
                  @click="openButtonUrl"
                >
                  {{ notification.button_text }}
                </button>
                <button
                  type="button"
                  :class="{'inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 sm:w-auto cursor-pointer': true, 'sm:mr-3': notification.button_text && notification.button_url, 'sm:mr-0': !(notification.button_text && notification.button_url)}"
                  @click="dismiss"
                >
                  {{ $t('common.got_it') }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { InformationCircleIcon } from '@heroicons/vue/24/outline'
import { dismissNotification, markNotificationAsShown } from '@/data/requests/notifications'
import { useStore } from 'vuex'

export default {
  name: 'ModalNotificationComponent',
  components: {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    InformationCircleIcon,
  },
  props: {
    notification: {
      type: Object,
      required: true
    }
  },
  emits: ['dismissed'],
  setup(props, { emit }) {
    const store = useStore()
    const isVisible = ref(false)
    
    const user = computed(() => store.getters['user/user'])
    const userUrl = computed(() => {
      if (user.value?.username) {
        return `${user.value.username}.gallera.io`
      }
      return 'myname.gallera.io'
    })

    const dismiss = () => {
      dismissNotification(
        props.notification.key,
        () => {
          isVisible.value = false
          emit('dismissed', props.notification.key)
        },
        (type, data) => {
          console.error('Помилка при відмітці сповіщення:', type, data)
          // Все одно приховуємо сповіщення навіть якщо запит не вдався
          isVisible.value = false
          emit('dismissed', props.notification.key)
        }
      )
    }

    const openButtonUrl = () => {
      if (props.notification.button_url) {
        window.open(props.notification.button_url, '_blank', 'noopener,noreferrer')
      }
    }

    onMounted(() => {
      // Позначаємо сповіщення як показане
      markNotificationAsShown(
        props.notification.key,
        () => {
          // Успішно позначено як показане
        },
        (type, data) => {
          console.error('Помилка при позначенні сповіщення як показаного:', type, data)
        }
      )

      // Показуємо сповіщення з невеликою затримкою для плавної анімації
      setTimeout(() => {
        isVisible.value = true
      }, 100)
    })

    return {
      isVisible,
      dismiss,
      openButtonUrl,
      userUrl
    }
  }
}
</script>


